/**
 * @module core/aem
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
/** */

// tslint:disable max-file-line-count
import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { Subject } from 'rxjs';
import { UntypedFormGroup, UntypedFormBuilder } from '@angular/forms';
// Services
import { ProductDetailsService } from '@experience/shop/services/product-details.service';
import { CommonUtilService } from '@support/shared/services/common-util.service';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
// Interfaces
import { APIError } from '@support/api/common/error.interface';
import {
  ProductReviewInterface,
  ReviewFeedback,
  ReviewFeedBackResp,
  Reviews,
  SearchOptions,
} from '@core/aem/components/product-reviews/interface/product-review.interface';
import { NavigationStart, Router, ActivatedRoute } from '@angular/router';
import { ConfigData } from '@experience/repdash/interfaces/aem-content.interface';
import { DynamicModal } from '@support/shared/widgets/interfaces/dynamic-modal.interface';
import { ReviewsQuery } from '@experience/shop/product-details/models/review-query.model';
import { PRODUCT_DETAILS_CONST } from '@experience/shop/product-details/product-details.constants';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { ScrollListenerService } from '@support/shared/services/scroll-listener.service';
import { HARDGOODS_CONSTANTS } from '@experience/shop/hardgoods/constants/browse-hardgoods.constants';
import { WindowRefService } from '@support/shared/services/window-ref.service';
import { AppConfig } from '@root/app.config';
import { takeUntil } from 'rxjs/operators';
/**
 * Product Specific Component initiator
 * @export
 * @class ProductSpecificationComponent
 * @implements {OnInit}
 * @implements {OnDestroy}
 */
@Component({
  selector: 'tmo-product-reviews',
  templateUrl: './product-reviews.component.html',
  styleUrls: [
    '../../../../../style-sheets/components/textarea.component.scss',
    '../../../../../style-sheets/components/html-spinner.component.scss',
  ],
})
export class ProductReviewsComponent implements OnInit, OnDestroy {
  /**
   * Boolean to show or hide see all reviews cta
   */
  @Input() public seeAllCta: boolean;
  /**
   * Boolean to show limit of reviews
   */
  @Input() public limit: boolean;
  /**
   * Boolean to show limit of reviews
   */
  @Input() public reviewsStaticContent: ConfigData;
  /**
   * String to send type of reviews on landing page
   */
  @Input() public type: string;
  /**
   * boolean to enable infinite scroll
   */
  @Input() public scrollEnable: boolean;

  /**
   * Object to store filterData of type SearchOptions;
   */
  @Output() public filterData: EventEmitter<SearchOptions>;

  @Input() public set filterParams(value: ReviewsQuery) {
    if (this.queryParams && this.queryParams !== value) {
      this.dataFiltered = value.queryParams.filter !== '';
      this.queryParams = value;
      this.calculateStarRatingFilterCount();
      this.queryParams.setLimit(PRODUCT_DETAILS_CONST.REVIEW_ALL_LIMIT);
      this.getReviewsFromService(this.reviewsStaticContent);
    }
  }

  public queryParams: ReviewsQuery;
  /**
   * Variable to store the review content from AEM and API
   */
  public reviewsText: string;
  /**
   * Variable to store the product Reviewers Recommend from AEM and API
   */
  public productReviewersRecommend: string;
  /**
   * Variable to review API response
   */
  public reviewsData: ProductReviewInterface;

  /**
   * Modal related to close accounts
   */
  public modal: DynamicModal;

  /**
   * Modal related to close accounts
   */
  public feedbackModal: DynamicModal;

  /**
   * Flag to see open or close modal
   */
  public seeMoreModal: boolean;

  /**
   * Stores FormGroup
   */
  public modalFormGroup: UntypedFormGroup;

  /**
   * Flag to see open or close modal
   */
  public seeMoreSecondModal: boolean;
  /**
   * Event Emitter to trigger events on click of anchor
   */
  public triggerEvent: EventEmitter<Object>;
  /**
   * to distroy the event OnDestroy life cycle
   */
  public ngUnsubscribe: Subject<void>;
  /**
   * To enable/disable thumbs up
   */
  public disableThumbsUp: boolean[];
  /**
   * To enable/disable thumbs up
   */
  public disableThumbsDown: boolean[];
  /**
   *  To check spinner
   */
  public showSpinner: boolean;
  /**
   *  To store review values.
   */
  private review: Reviews;
  /**
   *  To store review array.
   */
  public reviewBundle: ReviewFeedback[];
  /**
   *  To store spinner on the scroll.
   */
  public showComponentSpinner: boolean;
  /**
   *  To know if the data is filtered
   */
  public dataFiltered: boolean;
  /**
   * Stores container for infinite scroll
   */
  public infiniteScrollContainer: Element;
  /**
   * stores boolean value if channel is web
   */
  // public channelWeb: boolean;
  /**
   * Stores complete window for infinite scroll
   */
  public windowContainer: Window;
  /**
   * Stores boolean whether to scroll to review section
   */
  public scrollToReview: boolean;
  /**
   * Stores boolean value of epin
   */
  public isEpin: boolean;

  /**
   * Stores count for filters selected
   */
  public filteredReviewsCount: number;

  constructor(
    private stateUtilService: StateStoreUtilsService,
    private productDetailsService: ProductDetailsService,
    private commonUtilService: CommonUtilService,
    private objectService: ObjectService,
    private activatedRoute: ActivatedRoute,
    private fb: UntypedFormBuilder,
    private router: Router,
    private scrollListenerService: ScrollListenerService,
    private windowRefService: WindowRefService,
    private appConfig: AppConfig
  ) {
    this.filterData = new EventEmitter();
    this.ngUnsubscribe = new Subject<void>();
    this.seeAllCta = true;
    this.seeMoreModal = false;
    this.seeMoreSecondModal = false;
    this.modal = {
      disableDismiss: false,
      disableEsc: true,
      toggleModalEmitter: new EventEmitter(),
      dismissableMask: true,
      modalName: 'product-reviews-modal',
    };

    this.feedbackModal = {
      disableDismiss: false,
      disableEsc: true,
      toggleModalEmitter: new EventEmitter(),
      dismissableMask: true,
      modalName: 'feedback-modal',
    };
    this.scrollToReview = false;
    this.filteredReviewsCount = 0;
  }

  /**
   * Component lifecycle hook to initialize the component
   * @memberof ProductSpecificationComponent
   */
  public ngOnInit(): void {
    this.reviewBundle = [];
    // this.channelWeb = SETTINGS.CHANNEL.toLocaleLowerCase() === HARDGOODS_CONSTANTS.WEB;
    this.infiniteScrollContainer = this.scrollListenerService.getInfiniteScroll();
    this.isEpin =
      this.stateUtilService.getProductType() &&
      this.stateUtilService.getProductType().toLocaleLowerCase() === HARDGOODS_CONSTANTS.FINANCIAL;
    this.windowContainer = this.windowRefService.nativeWindow();
    if (!this.reviewsStaticContent) {
      this.productDetailsService
        .getProductDetailsContent()
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe((content: ConfigData) => {
          this.reviewsStaticContent = content;
          this.limit = true;
          this.getProductReviews(this.reviewsStaticContent);
        });
    } else {
      this.getProductReviews(this.reviewsStaticContent);
    }
    this.modalFormGroup = this.fb.group({
      reviewFeedBack: [],
    });
    this.router.events.pipe(takeUntil(this.ngUnsubscribe)).subscribe((event) => {
      if (event instanceof NavigationStart) {
        const offerId = this.stateUtilService.getSelectedOfferId();
        if (this.reviewBundle.length > 0) {
          this.showSpinner = true;
          this.productDetailsService
            .postReviewFeedback(offerId, this.reviewBundle)
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(
              (data: ReviewFeedBackResp[]) => {
                this.showSpinner = false;
              },
              (error: APIError) => {
                this.showSpinner = false;
              }
            );
        }
      }
    });
  }

  /**
   * Method to handle Click event
   * @returns void
   */
  public closeModal(): void {
    this.modal.toggleModalEmitter.emit({
      open: false,
      close: true,
    });
    this.seeMoreModal = false;
  }

  /**
   * Method to handle Click event
   * @returns void
   */
  public closeFeedbackModal(): void {
    this.feedbackModal.toggleModalEmitter.emit({
      open: false,
      close: true,
    });
    this.seeMoreSecondModal = false;
  }
  /**
   * Method to get reviews data from api_2145
   * @param content: ConfigData
   */
  public getProductReviews(content: ConfigData): void {
    if (!this.queryParams && this.limit) {
      this.queryParams = new ReviewsQuery(PRODUCT_DETAILS_CONST.LANDING);
      this.queryParams.setLimit(PRODUCT_DETAILS_CONST.REVIEW_PDP_LIMIT);
    } else {
      this.queryParams = new ReviewsQuery(PRODUCT_DETAILS_CONST.DETAILS);
      this.queryParams.setLimit(PRODUCT_DETAILS_CONST.REVIEW_ALL_LIMIT);
    }
    this.getReviewsFromService(content);
  }

  /**
   * method to redirect to review page
   */
  public navigateToReview(): void {
    this.router.navigate(['product-details/read-reviews']);
  }
  /**
   * Method to parse and navigate to write review page
   * @returns voidd
   */
  public navigateToBazaarVoiceWriteReview(): void {
    const productOfferingId = this.stateUtilService.getSelectedOfferId();
    let bazaarvoiceWriteReviewUrl = '';
    const win = window;
    const routeUrl = HARDGOODS_CONSTANTS.WRITE_REVIEW_URL;
    if (!!this.appConfig.getConfig(HARDGOODS_CONSTANTS.BAZAAR_WRITE_REVIEW_URL_KEY)) {
      bazaarvoiceWriteReviewUrl = this.appConfig
        .getConfig(HARDGOODS_CONSTANTS.BAZAAR_WRITE_REVIEW_URL_KEY)
        .replace('[EPC_PRODUCT_OFFERING_ID]', productOfferingId);
    }
    const submissionurl = encodeURIComponent(
      `${win.location.protocol}//${win.location.host}/${routeUrl}`
    );
    bazaarvoiceWriteReviewUrl = bazaarvoiceWriteReviewUrl
      .concat('?submissionurl=')
      .concat(submissionurl);
    win.open(bazaarvoiceWriteReviewUrl);
  }

  private getReviewsFromService(content: ConfigData): void {
    this.showComponentSpinner = true;
    const offerId = this.stateUtilService.getSelectedOfferId();
    this.productDetailsService
      .getDeviceReviews(offerId, this.queryParams)
      .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
      .subscribe(
        (response: ProductReviewInterface) => {
          this.showComponentSpinner = false;
          this.scrollEnable =
            this.objectService.get(response, 'reviews.length') ===
            PRODUCT_DETAILS_CONST.REVIEW_ALL_LIMIT;
          if (!this.reviewsData || this.dataFiltered) {
            this.reviewsData = response;
          } else {
            response.reviews.forEach((review: Reviews) => {
              this.reviewsData.reviews.push(review);
            });
          }
          this.filterData.emit(response.searchOptions);
          const reviews = this.objectService.get(response, 'totalReviews');
          if (reviews) {
            this.reviewsText = this.commonUtilService.messageFormatter(content.numberOfreviews, [
              reviews.toString(),
            ]);
          }
          if (response.reviewsRecommendRate) {
            this.productReviewersRecommend = this.commonUtilService.messageFormatter(
              content.productReviewersRecommend,
              [response.reviewsRecommendRate.toString()]
            );
          }
          if (this.reviewsData && this.reviewsData.reviews) {
            this.disableThumbsUp = Array(this.reviewsData.reviews.length);
            this.disableThumbsDown = Array(this.reviewsData.reviews.length);
            this.reviewsData.reviews.forEach((review: Reviews) => {
              review.intialHelpfulCount = review.helpful;
              review.intialUnhelpfulCount = review.unhelpful;
            });
          }
          this.scrollToReview = this.objectService.get(
            this.activatedRoute,
            'snapshot.queryParams.scrollToReview'
          );
        },
        (error: APIError) => {
          this.showComponentSpinner = false;
        }
      );
  }

  /**
   * Method to calculate total reviews for selected ratings
   * @returns void
   */
  private calculateStarRatingFilterCount(): void {
    if (
      this.dataFiltered &&
      this.objectService.get(this.reviewsData, 'searchOptions.closeFilter')
    ) {
      const filterSelected = this.queryParams.queryParams.filter;
      const filterValues = filterSelected.split(',');
      const starRatingsKeys = {
        5: 'fiveStarCounts',
        4: 'fourStarCounts',
        3: 'threeStarCounts',
        2: 'twoStarCounts',
        1: 'oneStarCounts',
      };
      let totalFilterCount = 0;
      for (let i = 0; i < filterValues.length; i++) {
        const starRatingKeyName = starRatingsKeys[filterValues[i]];
        const filterCount = this.reviewsData.searchOptions.closeFilter[starRatingKeyName] || 0;
        const radix = 10;
        totalFilterCount = totalFilterCount + parseInt(filterCount, radix);
      }
      this.filteredReviewsCount = totalFilterCount;
    }
  }
  /**
   * Showing of modal on toggle event
   * @param event : toggle event
   * @returns void
   */
  public showAppropriateModal(index: number, event?: EventEmitter<Object>): void {
    this.review = this.objectService.get(this.reviewsData, 'reviews.'.concat(index.toString()));
    this.seeMoreModal = true;
    this.triggerEvent = event;
    this.modal.toggleModalEmitter.emit({
      open: true,
      close: false,
    });
  }

  /**
   * Showing of modal on toggle event
   * @param event : toggle event
   * @returns void
   */
  public showFeedbackModal(event?: EventEmitter<Object>): void {
    this.seeMoreSecondModal = true;
    this.triggerEvent = event;
    this.feedbackModal.toggleModalEmitter.emit({
      open: true,
      close: false,
    });
    const reasonText = this.modalFormGroup.controls.reviewFeedBack.value;
    this.closeModal();
    this.createFeedbackbundle(
      this.objectService.get(this.review, 'id'),
      'inappropriate',
      null,
      reasonText
    );
    this.modalFormGroup.controls.reviewFeedBack.setValue('');
  }
  /**
   * functionality for thumbs up and down
   * @param action : string, index : number; id =string
   * @returns void
   */
  public thumbCTAClick(action: string, index: number, id: string): void {
    const review = this.reviewsData.reviews[index];
    const feedBaackType = 'helpfullness';
    if (action === 'thumbsUp') {
      if (this.disableThumbsDown[index]) {
        this.disableThumbsDown[index] = false;
        review.helpful--;
        this.removeFeedback(id, feedBaackType, true);
      } else {
        review.helpful++;
        this.createFeedbackbundle(id, feedBaackType, true);
        this.disableThumbsUp[index] = false;
        this.disableThumbsDown[index] = true;
      }
      if (review.reviewVisited && review.intialUnhelpfulCount !== review.unhelpful) {
        review.unhelpful--;
        this.removeFeedback(id, feedBaackType, false);
      }
    } else {
      if (this.disableThumbsUp[index]) {
        this.disableThumbsUp[index] = false;
        review.unhelpful--;
        this.removeFeedback(id, feedBaackType, false);
      } else {
        review.unhelpful++;
        this.createFeedbackbundle(id, feedBaackType, false);
        this.disableThumbsDown[index] = false;
        this.disableThumbsUp[index] = true;
      }
      if (review.reviewVisited && review.intialHelpfulCount !== review.helpful) {
        review.helpful--;
        this.removeFeedback(id, feedBaackType, true);
      }
    }
    review.reviewVisited = true;
  }
  /**
   * Unsubscribes for all subscriptions
   * @memberof ProductSpecificationComponent
   */
  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  /**
   * Functionality for loading the reviews on page scroll
   * @returns void
   */
  public onPageScroll(): void {
    if (this.scrollEnable && !this.showComponentSpinner) {
      const diffReviews = this.reviewsData.totalReviews - this.reviewsData.reviews.length;
      const limit =
        diffReviews > PRODUCT_DETAILS_CONST.REVIEW_ALL_LIMIT
          ? PRODUCT_DETAILS_CONST.REVIEW_ALL_LIMIT
          : diffReviews;
      if (diffReviews > 0) {
        this.queryParams.setOffset(this.reviewsData.reviews.length);
        this.queryParams.setLimit(limit);
        this.getReviewsFromService(this.reviewsStaticContent);
      }
    }
  }

  /**
   * functionality for creating Feedbackbundle
   * @param id: string, feedbackType: string, reasonText?: string
   * @returns void
   */
  private createFeedbackbundle(
    id: string,
    feedbackType: string,
    vote?: boolean,
    reasonText?: string
  ): void {
    const filteredReview = this.reviewBundle.filter((review: ReviewFeedback) => {
      return review.reviewId === id && review.feedbackType === feedbackType;
    });
    if (filteredReview.length !== 1) {
      this.reviewBundle.push({
        reviewId: id,
        feedbackType: feedbackType,
        reasonText: reasonText,
        vote: vote,
      });
    }
  }

  /**
   * functionality for remove Feedback
   * @param id: string, feedBack: string
   * @returns void
   */
  private removeFeedback(id: string, feedBack: string, vote: boolean): void {
    const filteredReview = this.reviewBundle.filter((review: ReviewFeedback) => {
      return !(review.reviewId === id && review.feedbackType === feedBack && review.vote === vote);
    });
    this.reviewBundle = filteredReview;
  }
}
